import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button, Grid, TextField } from "@mui/material";
import { useGetProjectsQuery } from "../../services/api";
import Project from "./Project";
import ProjectsSkeleton from "./ProjectsSkeleton";

export const TABS = {
  FAV: 0,
  ME: 1,
  ALL: 2,
  SEARCH: 3,
  ACTIVE: 4,
};

const TabPanel = ({ value, index, children }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && (
      <Grid container spacing={2}>
        {children}
      </Grid>
    )}
  </div>
);

const ProjectsList = ({ projects = [], loading, onEdit, error }) => {
  const navigate = useNavigate();

  // Handle 401 errors by redirecting to login
  useEffect(() => {
    if (error?.status === 401) {
      navigate("/login");
    }
  }, [error, navigate]);

  if (loading) return <ProjectsSkeleton />;

  if (!projects.length) {
    return (
      <Grid item xs={12}>
        <div style={{ paddingTop: 18, textAlign: "center" }}>No reports</div>
      </Grid>
    );
  }

  return projects.map((project) => (
    <Project key={project._id} project={project} onEdit={onEdit} />
  ));
};

export const FavoriteTab = ({ tabIndex, onProjectEdit }) => {
  const {
    data: projects = [],
    isLoading,
    error,
  } = useGetProjectsQuery(
    { type: "favorites" },
    { skip: tabIndex !== TABS.FAV },
  );

  return (
    <TabPanel value={tabIndex} index={TABS.FAV}>
      <ProjectsList
        projects={projects}
        loading={isLoading}
        onEdit={onProjectEdit}
        error={error}
      />
    </TabPanel>
  );
};

export const MyReportsTab = ({ tabIndex, onProjectEdit }) => {
  const {
    data: projects = [],
    isLoading,
    error,
  } = useGetProjectsQuery({ type: "me" }, { skip: tabIndex !== TABS.ME });

  return (
    <TabPanel value={tabIndex} index={TABS.ME}>
      <ProjectsList
        projects={projects}
        loading={isLoading}
        onEdit={onProjectEdit}
        error={error}
      />
    </TabPanel>
  );
};

export const AllTab = ({ tabIndex, onProjectEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get("page")) || 1;
  const [page, setPage] = useState(initialPage);

  const {
    data: projects = [],
    isLoading,
    error,
  } = useGetProjectsQuery(
    { type: "all", page },
    { skip: tabIndex !== TABS.ALL },
  );

  const handlePageChange = (event) => {
    const value = parseInt(event.target.value);
    if (value > 0) {
      updatePage(value);
    }
  };

  const updatePage = (value) => {
    setPage(value);
    setSearchParams({ page: value.toString() });
  };

  return (
    <TabPanel value={tabIndex} index={TABS.ALL}>
      <ProjectsList
        projects={projects}
        loading={isLoading}
        onEdit={onProjectEdit}
        error={error}
      />
      <Grid container spacing={2} style={{ marginTop: "2em" }}>
        <Grid item xs={12} sm={4}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={page <= 1}
            onClick={() => updatePage(page - 1)}
          >
            &lt; Previous
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Page"
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            value={page}
            onChange={handlePageChange}
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => updatePage(page + 1)}
          >
            Next &gt;
          </Button>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export const SearchTab = ({ tabIndex, onProjectEdit }) => {
  const [searchParams] = useSearchParams();
  const term = searchParams.get("term");

  const {
    data: projects = [],
    isLoading,
    error,
  } = useGetProjectsQuery(
    { search: term },
    { skip: tabIndex !== TABS.SEARCH || !term },
  );

  return (
    <TabPanel value={tabIndex} index={TABS.SEARCH}>
      <ProjectsList
        projects={projects}
        loading={isLoading}
        onEdit={onProjectEdit}
        error={error}
      />
    </TabPanel>
  );
};

export const ActiveTab = ({ tabIndex, onProjectEdit }) => {
  const {
    data: projects = [],
    isLoading,
    error,
  } = useGetProjectsQuery(
    { type: "active" },
    { skip: tabIndex !== TABS.ACTIVE },
  );

  return (
    <TabPanel value={tabIndex} index={TABS.ACTIVE}>
      <ProjectsList
        projects={projects}
        loading={isLoading}
        onEdit={onProjectEdit}
        error={error}
      />
    </TabPanel>
  );
};
